import { useRoute } from "vue-router"
import { ref, watch, onMounted } from "vue"
import { main_menu } from "~/common/main_menu"
import { useI18n } from "vue-i18n"

export function useAutoBreadcrumbs() {
  const route = useRoute()
  const breadCrumbs = ref<any[]>([])
  const i18n = useI18n()

  // Función para encontrar el elemento de menú correspondiente a una ruta
  function findMenuItemByPath(path: string, menu = main_menu): any | undefined {    
    // Búsqueda exacta en el menú
    for (const item of menu) {
      if (item.route === path) return item
      if (item.children) {
        const found = findMenuItemByPath(path, item.children)
        if (found) return found
      }
    }
    
    return undefined
  }

  // Función para obtener el título de un segmento de ID
  function getIdSegmentTitle(id: string, parentPath: string): string {
    // Aquí podrías implementar lógica para obtener el nombre real del recurso si es necesario
    return id
  }

  // Agregar esta nueva función para buscar elementos con breadCrumbRoute
  function findBreadCrumbRouteParent(path: string, menu = main_menu): any | undefined {
    let bestMatch = undefined;
    let maxLength = 0;

    function search(items: any[]) {
      for (const item of items) {
        if(item.breadCrumbRoute && (item.children?.some((i: any) => i.route?.toString().startsWith(path)))){
          bestMatch = item;
          maxLength = 9999
        }
        
        // Si la ruta actual incluye la ruta del ítem y el ítem tiene breadCrumbRoute
        if (item.route && path.startsWith(item.route) && item.route.length > maxLength) {
          bestMatch = item;
          maxLength = item.route.length;
        }
        
        // Buscar en los hijos también
        if (item.children) {
          search(item.children);
        }
      }
    }
    
    search(menu);
    return bestMatch;
  }

  const isNumericId = (segment: string) => /^\d+$/.test(segment)
  const isIdSegment = (segment: string) => isNumericId(segment) || segment.includes('-')
  const isActionSegment = (segment: string) => ['create', 'edit', 'show'].includes(segment)
  const getActionIcon = (segment: string) => {
    switch (segment) {
      case 'create': return 'sym_o_add'
      case 'edit': return 'sym_o_edit'
      case 'show': return 'sym_o_visibility'
      default: return undefined
    }
  }
  const getActionLabel = (segment: string, i18n: any) => {
    switch (segment) {
      case 'create': return i18n.t('breadcrumbs.create')
      case 'edit': return i18n.t('breadcrumbs.edit')
      case 'show': return i18n.t('breadcrumbs.show')
      default: return segment
    }
  }

  const handleMenuOrParent = (segment: string, accumulatedPath: string, i18n: any) => {
    const menuItem = findMenuItemByPath(accumulatedPath)
    if (menuItem) {
      return {
        labelKey: i18n.t(menuItem.title),
        to: accumulatedPath,
        icon: menuItem.icon
      }
    }
    const breadCrumbParent = findBreadCrumbRouteParent(accumulatedPath)
    if (breadCrumbParent) {
      return {
        labelKey: i18n.t(breadCrumbParent.title),
        to: accumulatedPath,
        icon: breadCrumbParent.icon
      }
    }
    return {
      labelKey: i18n.t('breadcrumbs.' + segment) || segment,
      to: accumulatedPath
    }
  }

  const generateBreadCrumbs = () => {
    const newBreadCrumbs = []
    const pathSegments = route.path.split("/").filter(Boolean)
    let accumulatedPath = ""

    // Agregar breadcrumb de inicio
    newBreadCrumbs.push({
      labelKey: i18n.t('nav.manage'),
      to: null,
      icon: 'sym_o_work'
    })

    for (let i = 1; i < pathSegments.length; i++) {
      const segment = pathSegments[i]
      const prevAccumulatedPath = accumulatedPath
      accumulatedPath += "/" + segment
      if (i === 1) {
        accumulatedPath = "/" + pathSegments[0] + "/" + segment
      }

      if (isIdSegment(segment)) {
        newBreadCrumbs.push({
          labelKey: getIdSegmentTitle(segment, prevAccumulatedPath),
          to: accumulatedPath
        })
      } else if (isActionSegment(segment)) {
        newBreadCrumbs.push({
          labelKey: getActionLabel(segment, i18n),
          to: accumulatedPath,
          icon: getActionIcon(segment)
        })
      } else {
        newBreadCrumbs.push(handleMenuOrParent(segment, accumulatedPath, i18n))
      }
    }
    return newBreadCrumbs
  }

  onMounted(() => breadCrumbs.value = generateBreadCrumbs())

  watch(() => route.path, () => {
    breadCrumbs.value = generateBreadCrumbs()
  })

  return { breadCrumbs }
}